import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { AppDispatch, RootState } from "../../configureStore";
import {
    checkHealth,
    disableReceiver,
    enableReceiver,
    getLogs,
    getReceivers,
    RESET_RETURN_OPS,
} from "../../redux/reducer/returnOps";
import HealthCheckCard from "../molecules/HealthCheckCard";
import ReceiversTable from "../organisms/ReceiversTable";
import BackendLogs from "../organisms/BackendLogs";

const ReturnOps = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const {
        healthStatus,
        receivers,
        logs,
        loadingHealth,
        loadingReceivers,
        loadingLogs,
        error,
    } = useSelector<RootState, RootState["returnOps"]>((state) => state.returnOps);

    useEffect(() => {
        dispatch(checkHealth());
        dispatch(getReceivers());
        dispatch(getLogs());

        return () => {
            dispatch({ type: RESET_RETURN_OPS });
        };
    }, [dispatch]);

    const handleReceiverToggle = useCallback((receiverId: string, enabled: boolean) => {
        if (enabled) {
            dispatch(disableReceiver({ input: { receiverId } }));
        } else {
            dispatch(enableReceiver({ input: { receiverId } }));
        }
    }, [dispatch]);

    const handleRefreshHealth = useCallback(() => {
        dispatch(checkHealth());
    }, [dispatch]);

    const handleRefreshReceivers = useCallback(() => {
        dispatch(getReceivers());
    }, [dispatch]);

    const handleRefreshLogs = useCallback(() => {
        dispatch(getLogs());
    }, [dispatch]);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <IconButton onClick={() => navigate("/returns")} sx={{ mr: 2 }}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    Backend Management
                </Typography>
            </Box>

            {error && (
                <Box sx={{ mb: 2 }}>
                    <Typography color="error">
                        {typeof error === 'string' ? error : (error as any)?.message || JSON.stringify(error)}
                    </Typography>
                </Box>
            )}

            <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                    <HealthCheckCard
                        healthStatus={healthStatus}
                        loading={loadingHealth}
                        onRefresh={handleRefreshHealth}
                    />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <ReceiversTable
                        receivers={receivers}
                        loading={loadingReceivers}
                        onRefresh={handleRefreshReceivers}
                        onToggle={handleReceiverToggle}
                    />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <BackendLogs
                        logs={logs}
                        loading={loadingLogs}
                        onRefresh={handleRefreshLogs}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default ReturnOps;
